/** @prettier */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Box, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import Page from 'src/components/Page';
import useDealers from 'src/hooks/useDealers';
import { setClaimInfo, updateMember, setDealerInfo, setOrderInfo } from 'src/actions/claimActions';
import { updateDealer, updateUserData } from 'src/actions/accountActions';
import claimService from 'src/services/claimService';
import ClaimCreationSummary from './claim-summary';
import ProductComingSoon from './product-coming-soon';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { ClaimProvider } from 'src/context/ClaimContext';
import { getClaimSourceKeyFor } from 'src/utils/claimSource';
import ClaimReview from './ClaimReview';
import WorkflowStepper from './WorkflowStepper';
import ContactVerification from './ContactVerification';
import ClaimTypeComponentMap from './claimTypeComponentMap';
import { CheckCircleOutline } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),

    '& .MuiFormLabel-asterisk': {
      color: '#E50046',
      fontSize: theme.typography.pxToRem(18)
    }
  },
  box: {
    color: theme.palette.text.primary.main,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    borderRadius: 5
  },

  claimTypeContainer: {
    backgroundColor: 'red',
    marginTopTop: '100px',
    height: '100px'
  },
  successNotiStack: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    borderRadius: 12,
    padding: 0,
    paddingLeft: theme.spacing(1),
    minWidth: '240px',
    paddingRight: theme.spacing(2),
  },
  successNotiStackIcon: {
    color: '#54f05a',
    marginRight: theme.spacing(1),
    fontSize: 28,
    verticalAlign: 'middle',
  },
}));

const ExternalClaimWizardInner = ({ className }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ClaimWorkFlowEnum = {
    CONTACT_VERIFICATION: 123,
    CLAIM_TYPE: 124,
    CLAIM_REVIEW: 125,
    CONFIRMATION: 126,
  };
  const initWorkflowState = {
    workflowStepName: ClaimWorkFlowEnum.CONTACT_VERIFICATION,
    workflowStepNumber: 0
  };

  const pageRef = useRef();
  const classes = useStyles();
  const { activeDealer } = useDealers();
  const dispatch = useDispatch();

  const [workflowStack, setWorkflowStack] = useState([initWorkflowState]);
  const dealerUser = useSelector(state => state.dealerAccount.dealerUser);
  const { dealerInfo } = useSelector(state => state.claims);
  const { orderInfo } = useSelector(state => state.claims);
  const { claimInfo } = useSelector(state => state.claims);
  const [hideSteps, setHideSteps] = useState(false);
  const [claimContactInfo, setClaimContactInfo] = useState({});

    const steps = [t('verifyContact'), t('createClaim'), t('reviewClaim'), t('confirmation')];

  useEffect(() => {
    const storedDealerInfo = localStorage.getItem('dealerInfo');
    const storedOrderInfo = localStorage.getItem('orderInfo');

    if (storedDealerInfo) {
      dispatch(setDealerInfo(JSON.parse(storedDealerInfo)));
    }
    if (storedOrderInfo) {
      dispatch(setOrderInfo(JSON.parse(storedOrderInfo)));
    }
  }, [dispatch]);

  /** 
   * @param {string} stepName 
   * @param {boolean} incrementStepNumber
  */
  const pushOntoWorkflowStack = (stepName, incrementStepNumber = true) => {
    const stackCopy = [...workflowStack];
    let { workflowStepNumber } = stackCopy.at(-1)
    workflowStepNumber += incrementStepNumber ? 1 : 0;

    stackCopy.push({
      workflowStepNumber,
      workflowStepName: stepName,
    });
    setWorkflowStack(stackCopy);
  }

  const handleVerifyContactInfo = ({ email, firstName, lastName, phoneNumber }) => {
    pushOntoWorkflowStack(ClaimWorkFlowEnum.CLAIM_TYPE);
    const contactName = firstName + ' ' + lastName;
    setClaimContactInfo({ emailAddress: email, contactName: contactName, phoneNumber });
    dispatch(updateUserData({
      email
      , firstName
      , lastName
      , phoneNumber
      , fullName: contactName
    }));
  }

  const popFromWorkflowStack = () => {
    const stackCopy = [...workflowStack];
    stackCopy.pop();
    setWorkflowStack(stackCopy);
  }

  //Generate the claim contact info from the member policy

  /**
   * @param {Record<string, {coverage: number, causeOfLoss: string}>} claimRequest 
   * @returns {Promise<{claim: {claimNumber: number, claimId: number}}>}
   */
  const makeClaimPromises = (claimRequest) => {
    const claimPromises = [];
    const claimSource = 'VENDOR';
    const claimAppId = 2;
    const vendorGlassClaimTypeId = 11


    let clmRequest = {
      claimSource,
      claimAppId: claimAppId,
      purchaseOrderId: orderInfo.vendorPurchaseOrderId,
      claimNotes: claimRequest.claimNotes,
      causeOfLoss: claimRequest.causeOfLossId,
      dateOfLoss: claimRequest.claimDetail.dateOfLoss,
      createdBy: claimContactInfo.emailAddress,
      claimTypeId: vendorGlassClaimTypeId,
      dealerCode: dealerInfo?.dealerCode ?? '159822',
      claimDetail: claimRequest.claimDetail,
      referenceNumber: claimRequest.referenceNumber,
      ...claimContactInfo,
    };

    claimPromises.push(claimService.makeVendorClaim(clmRequest));
    return Promise.all(claimPromises);
  }

  /**
   * @param {{claimNumber: number, claimId: number}} claimNumbers 
   */
  const handleCreateClaimSuccess = (claimNumbers) => {
    dispatch(setClaimInfo({ claimNumbers }));
    pushOntoWorkflowStack(ClaimWorkFlowEnum.CONFIRMATION);
      enqueueSnackbar(t('claimCreatedSuccessfully'), { variant: 'success' });
    setHideSteps(true);
  }

  const handleCreateClaimError = (error) => {
    enqueueSnackbar(error.toString(), { variant: 'error' });
  }

  /** @param {() => {}} onReviewClaim */
  const handleReviewClaim = (onReviewClaim) => {
    if (onReviewClaim) onReviewClaim();
    pushOntoWorkflowStack(ClaimWorkFlowEnum.CLAIM_REVIEW);
    pageRef.current.parentElement.scrollTo(0, 0)
  }

  /**
   * @param {Record<string, {coverage: number, causeOfLoss: string, claimSource: string}>} claimRequest 
   */
  const handleClaimCreation = async (claimRequest) => {
    try {
      claimRequest.claimAppId = 2; //DealerPortal CalimAppId


      const claimNumbers = [];
      const claimResponses = await makeClaimPromises(claimRequest);

      for (const resp of claimResponses) {
        claimNumbers.push({
          claimNumber: resp.claim.vendorClaimId,
          claimId: resp.claim.vendorClaimId,
          qoreClaimId: resp.claim.vendorClaimId,
          claimSource: getClaimSourceKeyFor(resp.claim.claimSource),
          authorizationCode: null,
          confirmationOnly: false,
          isDenied: resp.claim.isDenied ?? false,
          deniedCoverageDetails: resp.claim.deniedCoverageDetails ?? [],
          dealerInfo: dealerInfo,
          orderInfo: orderInfo
        });
      }
      handleCreateClaimSuccess(claimNumbers);

    } catch (error) {
      handleCreateClaimError(error);
    }
  };

  const goBack = () => {
    popFromWorkflowStack();
  };

  useEffect(() => {
    if (dealerInfo) {
      localStorage.setItem('dealerInfo', JSON.stringify(dealerInfo));
    }
  }, [dealerInfo]);

  useEffect(() => {
    if (orderInfo) {
      localStorage.setItem('orderInfo', JSON.stringify(orderInfo));
    }
  }, [orderInfo]);

  useEffect(() => {
    (async () => {
      if (activeDealer) {
        const states = await claimService.listOfCountryStates();
        const dealerState = states.CountryStates?.find(
          ({ stateId }) => stateId === activeDealer.stateId
        );
        const dealerInfo = {
          name: activeDealer.name,
          dealerAddress: activeDealer.businessAddress,
          dealerCity: activeDealer.city,
          dealerStateId: activeDealer.stateId,
          dealerZipCode: activeDealer.businessZip,
          dealerGroup: activeDealer.dealerGroup,
          dealerState
        };
        dealerInfo.dealerState = dealerState?.abbreviation ?? '';
        setPolicies([]);
        setSelectedVin('');
        dispatch(updateMember(dealerInfo));
        dispatch(updateDealer(dealerInfo));
      }
    })();
  }, [activeDealer]);




  function ProductClaimScreen() {
    const { workflowStepName } = workflowStack.at(-1);
    const ClaimTypeComponent = ClaimTypeComponentMap['GlassClaim'];

    switch (workflowStepName) {
      case ClaimWorkFlowEnum.CONTACT_VERIFICATION:
        return (
          <ContactVerification
            goBack={goBack}
            claimContactInfo={claimContactInfo}
            dealerInfo={dealerInfo}
            orderInfo={orderInfo}
            onSubmitSuccess={handleVerifyContactInfo}
          />
        );
      case ClaimWorkFlowEnum.CLAIM_TYPE:
        return (
          <ClaimTypeComponent
            dealerInfo={dealerInfo}
            orderInfo={orderInfo}
            claimContactInfo={claimContactInfo}
            onSubmitSuccess={handleReviewClaim}
            goBack={goBack}
          />
        );

      case ClaimWorkFlowEnum.CLAIM_REVIEW:
        return (
          <ClaimReview
            goBack={goBack}
            dealerInfo={dealerInfo}
            orderInfo={orderInfo}
            claimContactInfo={claimContactInfo}
            onSubmitClaim={handleClaimCreation}
          />
        );
      case ClaimWorkFlowEnum.CONFIRMATION:
        return <ClaimCreationSummary claims={claimInfo} />
      default:
        return (
          <ProductComingSoon
            goBack={goBack}
          />
        );
    }
  }

  const { workflowStepNumber } = workflowStack.at(-1);
  return (
    <Page ref={pageRef} className={classes.root} title={t('fileAClaim')}>
      <Container maxWidth="lg" className={clsx(classes.root, className)}>
        <Box mt={6} className={classes.box}>
          <WorkflowStepper
            isHidden={hideSteps}
            steps={steps}
            activeStep={workflowStepNumber}
          />
          <ClaimProvider>
            <ProductClaimScreen />
          </ClaimProvider>
        </Box>
      </Container>
    </Page>
  );
};

ExternalClaimWizardInner.propTypes = {
  className: PropTypes.string
};

const CreateExternalClaimWizard = (props) => {
  const classes = useStyles();
  return (
    <SnackbarProvider
      autoHideDuration={8000}
      maxSnack={1}
      classes={{
        variantSuccess: classes.successNotiStack,
      }}
      iconVariant={{
        success: <CheckCircleOutline className={classes.successNotiStackIcon} />
      }}
    >
      <ExternalClaimWizardInner {...props} />
    </SnackbarProvider>
  );
};

CreateExternalClaimWizard.propTypes = {
  className: PropTypes.string
};

export default CreateExternalClaimWizard;
